import { defineRoutes } from '@lognai/plugin-sdk';

export const pluginId = 'invoices';
export const routes = defineRoutes(pluginId, '/invoices', [
  { path: '/view/:invoiceId', component: () => import('./pages/InvoiceWorkspacePage').then((module) => ({ default: module.InvoiceWorkspacePage })), name: 'Invoice insight', permissions: ['invoices:invoice:read'], nav: { showInNav: false, audience: 'internal' } },
  {
    path: '',
    component: () =>
      import('./pages/InvoicesOverviewPage').then((module) => ({
        default: module.InvoicesOverviewPage,
      })),
    name: 'All invoices',
    description: 'All invoice operations',
    permissions: ['invoices:invoice:read'],
    nav: {
      icon: { name: 'LayoutDashboard', size: 20 },
      showInNav: true,
      order: 1,
      audience: 'internal',
    },
  },
  {
    path: '/incoming',
    component: () =>
      import('./pages/InvoiceListPages').then((module) => ({
        default: module.IncomingInvoicesPage,
      })),
    name: 'Incoming',
    description: 'Connected and partner invoice inbox',
    permissions: ['invoices:invoice:read'],
    nav: { icon: { name: 'Inbox', size: 20 }, showInNav: true, order: 2, audience: 'internal' },
  },
  {
    path: '/payables',
    component: () =>
      import('./pages/InvoiceListPages').then((module) => ({ default: module.PayablesPage })),
    name: 'Payables',
    description: 'Vendor invoices',
    permissions: ['invoices:invoice:read'],
    nav: {
      icon: { name: 'ArrowUpRight', size: 20 },
      showInNav: true,
      order: 3,
      audience: 'internal',
    },
  },
  {
    path: '/receivables',
    component: () =>
      import('./pages/InvoiceListPages').then((module) => ({ default: module.ReceivablesPage })),
    name: 'Receivables',
    description: 'Customer invoices',
    permissions: ['invoices:invoice:read'],
    nav: {
      icon: { name: 'ArrowDownLeft', size: 20 },
      showInNav: true,
      order: 4,
      audience: 'internal',
    },
  },
  {
    path: '/payments',
    component: () =>
      import('./pages/PaymentPages').then((module) => ({ default: module.PaymentsPage })),
    name: 'Payments',
    description: 'Incoming and outgoing payments, allocations, and reconciliation',
    permissions: ['invoices:payment:read'],
    nav: { icon: { name: 'Banknote', size: 20 }, showInNav: true, order: 5, audience: 'internal' },
  },
  {
    path: '/exceptions',
    component: () =>
      import('./pages/ExceptionsPage').then((module) => ({ default: module.ExceptionsPage })),
    name: 'Exceptions',
    description: 'Audit, approval, dispute, and reconciliation exceptions',
    permissions: ['invoices:invoice:read'],
    nav: {
      icon: { name: 'CircleAlert', size: 20 },
      showInNav: true,
      order: 6,
      audience: 'internal',
    },
  },
  {
    path: '/configuration',
    component: () =>
      import('./pages/InvoiceSettingsPage').then((module) => ({
        default: module.InvoiceConfigurationPage,
      })),
    name: 'Configuration',
    description: 'Company, billing, audit-rule, and approval configuration',
    permissions: ['invoices:settings:read'],
    nav: { icon: { name: 'Settings', size: 20 }, showInNav: true, order: 7, audience: 'internal' },
  },
  {
    path: '/settings',
    component: () =>
      import('./pages/InvoiceSettingsPage').then((module) => ({
        default: module.LegacyInvoiceSettingsRedirect,
      })),
    name: 'Invoice settings redirect',
    permissions: ['invoices:settings:read'],
    nav: { showInNav: false, audience: 'internal' },
  },
  {
    // Hosts the whole New Invoice flow. Its position within the flow travels in
    // the query string (?direction, ?method, ?step) so a refresh mid-flow does
    // not drop the user back on step one.
    path: '/new',
    component: () =>
      import('./pages/InvoiceCreatePages').then((module) => ({
        default: module.NewInvoiceFlowPage,
      })),
    name: 'New invoice',
    // Any one of these grants access (the guard is any-of). All three are listed
    // because the flow now hosts what used to be three separately-gated entry
    // points: manual drafting, document intake, and receivable creation.
    permissions: [
      'invoices:invoice:draft',
      'invoices:invoice:intake',
      'invoices:receivable:create',
      'invoices:finding:resolve',
    ],
    nav: { showInNav: false, audience: 'internal' },
  },
  {
    path: '/payables/new',
    component: () =>
      import('./pages/InvoiceCreatePages').then((module) => ({ default: module.NewPayablePage })),
    name: 'New payable',
    permissions: ['invoices:invoice:intake'],
    nav: { showInNav: false, audience: 'internal' },
  },
  {
    path: '/receivables/new',
    component: () =>
      import('./pages/InvoiceCreatePages').then((module) => ({
        default: module.NewReceivablePage,
      })),
    name: 'New receivable',
    permissions: ['invoices:receivable:create'],
    nav: { showInNav: false, audience: 'internal' },
  },
  {
    path: '/payables/intake/:sessionId',
    component: () =>
      import('./pages/InvoiceIntakeWorkspacePage').then((module) => ({
        default: module.InvoiceIntakeWorkspacePage,
      })),
    name: 'Payable document intake',
    permissions: ['invoices:invoice:intake'],
    nav: { showInNav: false, audience: 'internal' },
  },
  {
    path: '/incoming/:invoiceId',
    component: () =>
      import('./pages/InvoiceWorkspacePage').then((module) => ({
        default: module.InvoiceWorkspacePage,
      })),
    name: 'Incoming invoice',
    permissions: ['invoices:invoice:read'],
    nav: { showInNav: false, audience: 'internal' },
  },
  {
    path: '/payables/:invoiceId',
    component: () =>
      import('./pages/InvoiceWorkspacePage').then((module) => ({
        default: module.InvoiceWorkspacePage,
      })),
    name: 'Payable invoice',
    permissions: ['invoices:invoice:read'],
    nav: { showInNav: false, audience: 'internal' },
  },
  {
    path: '/receivables/:invoiceId',
    component: () =>
      import('./pages/InvoiceWorkspacePage').then((module) => ({
        default: module.InvoiceWorkspacePage,
      })),
    name: 'Receivable invoice',
    permissions: ['invoices:invoice:read'],
    nav: { showInNav: false, audience: 'internal' },
  },
  {
    path: '/payments/new',
    component: () =>
      import('./pages/PaymentIntakePage').then((module) => ({ default: module.PaymentIntakePage })),
    name: 'Record payment',
    permissions: ['invoices:payment:record'],
    nav: { showInNav: false, audience: 'internal' },
  },
  {
    path: '/payments/:paymentId',
    component: () =>
      import('./pages/PaymentPages').then((module) => ({ default: module.PaymentWorkspacePage })),
    name: 'Payment workspace',
    permissions: ['invoices:payment:read'],
    nav: { showInNav: false, audience: 'internal' },
  },
  {
    path: '/portal',
    component: () =>
      import('./pages/InvoicePortalPages').then((module) => ({
        default: module.InvoicePortalPage,
      })),
    name: 'Invoices',
    description: 'Restricted partner invoice inbox',
    permissions: ['invoices:portal:access'],
    nav: {
      icon: { name: 'Invoice', size: 20 },
      showInNav: true,
      order: 2,
      audience: 'partner',
    },
  },
  {
    path: '/portal/intake/:sessionId',
    component: () =>
      import('./pages/InvoiceIntakeWorkspacePage').then((module) => ({
        default: module.PartnerInvoiceIntakeWorkspacePage,
      })),
    name: 'Partner invoice document intake',
    permissions: ['invoices:portal:access'],
    nav: { showInNav: false, audience: 'partner' },
  },
  {
    path: '/portal/:exchangeId',
    component: () =>
      import('./pages/InvoicePortalPages').then((module) => ({
        default: module.InvoicePortalWorkspacePage,
      })),
    name: 'Shared invoice',
    permissions: ['invoices:portal:access'],
    nav: { showInNav: false, audience: 'partner' },
  },
  {
    path: '/:invoiceId',
    component: () =>
      import('./pages/InvoiceWorkspacePage').then((module) => ({
        default: module.InvoiceWorkspacePage,
      })),
    name: 'Legacy invoice workspace',
    permissions: ['invoices:invoice:read'],
    nav: { showInNav: false, audience: 'internal' },
  },
]);
